import { check } from '@business/api/auth';
import { ItemId } from '@business/entities/library';
import { PackageItem } from './../../../modules/timeMaterials/AddProductOverlay/AddProductOverlay';
import { CompactOrgResponse, OrgAddressResponse, OrgId } from '@business/entities/organization';
import { InvoiceDiscountData } from '@modules/common/InvoiceDiscount';
import { TransactionInvoiceResponse } from '../TransactionInvoice';
import { InvoiceSalesTaxData } from './../../../modules/common/InvoiceSalesTax/InvoiceSalesTax';
import { POAddressFormData } from './../../../modules/orders/EditPOPage/POForm';
import { CompactUserResponse, UserId } from './../account/UserResponse';
import { BaseResponse } from './../common/BaseResponse';
import { CompactContactResponse } from './../contact/ContactResponse';
import { SimpleOrgResponse } from './../organization/OrgResponse';
import { InvoiceSettingsResponse } from './../TransactionInvoice/TransactionInvoiceResponse';
import { TMStageId, TMStageResponse } from './TMStageResponse';
import {
  OrderTermResponse,
  OrderShippingOption,
  PurchaseOrderResponse,
  OrderVendorResponse,
  PurchaseOrderLineItem,
  OrderShipmentResponse,
  OrderProductResponse,
  LabourItemResponse,
} from '../order';
import { LabourTypeResponse } from '../labour-type';
import { TMBoardId } from './TimeMaterialBoardResponse';
import { FolderId } from '../file';
import { ProposalDiscountFormData, TmFeeFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { VeryCompactProposalResponse } from '../proposal';

export type TimeMaterialId = string;

export enum TimeMaterialStatus {
  open = 'open',
  completed = 'completed',
  canceled = 'canceled',
  draft = 'draft',
}
export enum TimeMaterialShippingOption {
  ready = 'ready',
  complete = 'complete',
}

export type TMSectionId = number;
export interface TMSectionResponse {
  id: TMSectionId;
  title: string;
  menuOrder: number;
}

export enum TimeMaterialAlertStatus {
  success = 'success',
  warning = 'warning',
  danger = 'danger',
}

export interface CompactTimeMaterialResponse extends BaseResponse {
  id: TimeMaterialId;
  name: string;
  description: string;
  organization: CompactOrgResponse;
  status: TimeMaterialStatus;
  stageId: TMStageId;
  stage: TMStageResponse;
  term: OrderTermResponse;
  orderNumber: string;
  total: number;
  balance: number;
  deliveryDate?: number;
  shippedDate?: number;
  billingEmail: string;
  shippingMethod: string;
  shippingAccountNo: string;
  trackId: string;
  billingAddress: OrgAddressResponse;
  shippingAddress: OrgAddressResponse;
  contact: CompactContactResponse;
  salesReps: UserId[];
  salesRepsNames: string[];
  saleRepsLocked?: boolean;
  saleRepsLockReason?: string | null;
  saleRepsLockDate?: string | null;
  shippingOption: OrderShippingOption;
  customerPoNo: string;
  purchaseOrders: PurchaseOrderResponse[];
  canEdit: boolean;
  cc: string;
  bcc: string;
  isHide: boolean;
  pendingVendors: OrderVendorResponse[];
  totalTrackedHours: number;
  totalTrackedCost: number;
  sections: TMSectionResponse[];
  boardId: TMBoardId;
  boardName: string;
  wmsStatus: string;
  projectManager?: CompactUserResponse;
  customerServiceRep?: CompactUserResponse;
  users: string[];
  trackingTime: boolean;
  dueDate?: number;
  installDate?: number;
  completion?: number;
  completedAt?: number;
  isFlagged: boolean;
  noActivityPast3Days: boolean;
  lastCommunication?: number;
  alertMessage: string;
  alertStatus: TimeMaterialAlertStatus;
  events: TMEventResposne[];
  subTotal: number;
  discountTotal: number;
}

export type VeryCompactTimeMaterialResponse = Pick<
  CompactTimeMaterialResponse,
  | 'id'
  | 'boardId'
  | 'boardName'
  | 'orderNumber'
  | 'name'
  | 'organization'
  | 'stage'
  | 'purchaseOrders'
  | 'status'
  | 'created'
  | 'updated'
  | 'isFlagged'
  | 'installDate'
  | 'noActivityPast3Days'
  | 'lastCommunication'
  | 'total'
  | 'projectManager'
  | 'salesReps'
  | 'alertMessage'
  | 'alertStatus'
  | 'customerServiceRep'
>;

export interface BoardTimeMaterialResponse extends CompactTimeMaterialResponse {
  warningAItems: PurchaseOrderLineItem[];
  warningSItems: PurchaseOrderLineItem[];
  shippedItems: PurchaseOrderLineItem[];
}

export interface TimeMaterialResponse extends CompactTimeMaterialResponse {
  estimate: OrderEstimateResponse;
  invoices: TransactionInvoiceResponse[];
  settings: InvoiceSettingsResponse;
  logs: TimeMaterialStageLogResponse[];
  shipments: OrderShipmentResponse[];
  requestedItems: string[];
  logo: string;
  folderId: FolderId;
  fees: TmFeeFormData[];
  proposal: VeryCompactProposalResponse;
  completion: number;
  missingInstallDate: boolean;
  hasTodoTasks: boolean;
}

export interface TimeMaterialStageLogResponse extends BaseResponse {
  id: string;
  stageId: TMStageId;
  timeMaterialId: TimeMaterialId;
  time: number;
  user: CompactUserResponse;
  contact: CompactContactResponse;
}

export enum EstimateApprovalAction {
  noCreateInvoice = 'noCreateInvoice',
  createSaveInvoice = 'createSaveInvoice',
  createSendInvoice = 'createSendInvoice',
}

export type OrderEstimateId = string;

export enum OrderEstimateStatus {
  accepted = 'accepted',
  rejected = 'rejected',
  pending = 'pending',
  draft = 'draft',
  sent = 'sent',
}

export enum ProductStatus {
  pending = 'pending',
  ordered = 'ordered',
  partial = 'partial',
  partialInstalled = 'partial_installed',
  received = 'received',
  returned = 'returned',
  installed = 'installed',
  delivered = 'delivered',
}

export interface OrderEstimateResponse extends BaseResponse {
  id: OrderEstimateId;
  name: string;
  number: string;
  status: OrderEstimateStatus;
  reason: string;
  importId: string;
  docNumber: string;
  totalAmount: number;
  subTotal: number;
  total: number;
  balance: number;
  memo: string;
  message: string;
  estimateDate: number;
  expirationDate: number;
  user: CompactUserResponse;
  items: EstimateLineItem[];
  discount: InvoiceDiscountData | ProposalDiscountFormData;
  salesTax: InvoiceSalesTaxData;
  totalSalesTax: number;
  totalDiscount: number;

  profit: number;
  margin: number;
  partsCost: number;
  labourCost: number;
  partsPrice: number;
  labourPrice: number;
  totalCost: number;
  // 'total'       => $this->total,
  labourHours: number;
  markup: number;
}
export type EstimateLineItemId = number;
export interface EstimateLineItem {
  id: EstimateLineItemId;
  productId: string;
  serviceId: string;
  classId: string;
  productTitle: string;
  productSku: string;
  quantity: number;
  unitPrice: number;
  description: string;
  vendorId: string;
  tierId: number;
  thumbnail: string;
  unitOfMeasure: string;
  taxable: boolean;
  //   labour?: number;
  //   labourType?: LabourTypeResponse;
  //   labourPrice?: number;
  //   labourCost: number;
  cost: number;
  updatedCost?: number;
  status: ProductStatus;
  received: number;
  labourItems: LabourItemResponse[];
  sectionId: TMSectionId | undefined;
  lineCopyId: string;
  issuedQuantity?: number;
  fulfilledQuantity?: number;
  warehouseStatus?: string;
  packageItems: EstimateLineItem[];
  hasParent?: boolean;
  title?: string;
  productImage: string | File | undefined;
  installedQty: number;
  isDiscount: boolean;
  isServicePhase: boolean;
  statusDate?: number;
  isPrivate: boolean;
  isDeleted: boolean;
  totalRequestedQty: number;
}

export interface ExtendedEstimateLineItem extends EstimateLineItem {
  selectedQuantity: number;
}

export interface TmTrackingProductResponse {
  productId: number;
  quantity: number;
}
export interface EstimateCopyLineItem extends EstimateLineItem {
  lineCopyId: string;
  product: OrderProductResponse;
}

export interface OrderShippingRequest {
  deliveryDate?: number;
  shippedDate?: number;
  shippingMethod: string;
  shippingAccountNo: string;
  trackId: string;
}

export interface OrderEmailTemplateRequest {
  to?: string;
  cc?: string;
  bcc?: string;
  body?: string;
  isSave?: boolean;
  files?: File[];
}

export interface TimeMaterialRequest extends OrderEmailTemplateRequest {
  orgId: OrgId;
  billingEmail: string;
  billingAddress?: string | POAddressFormData;
  memo: string;
  message: string;
  status: string;
  poNumber?: string;
  shippingAddress?: string | POAddressFormData;
}

export interface OrderCancelRequest {
  reason: string;
  deleteEstimate?: boolean;
  deleteInvoice?: boolean;
  deletePurchaseOrder?: boolean;
}

export interface OrderWithItemsResposne {
  id: TimeMaterialId;
  title: string;
  items: EstimateCopyLineItem[];
}

export type TmPhaseId = string;
export interface CompactTimeMaterialPhaseResponse {
  id: string;
  title: string;
  menuOrder: number;
  estCompletionDate: number;
  timeMaterialId: TimeMaterialId;
  isClosed: boolean;
  users: string[];
  isEditable: boolean;
}

export interface TimeMaterialPhaseResponse extends CompactTimeMaterialPhaseResponse {
  items: TimeMaterialPhaseItem[];
}

export interface TimeMaterialPhaseItem {
  id: string;
  lineItem: TimeMaterialPhaseLineItem;
  menuOrder: number;
}

export interface TimeMaterialPhaseLineItem {
  itemId: string;
  title: string;
  description: string;
  packageName: string;
  room: string;
  vendor: string;
  brand: string;
  qty: number;
  attachedQty: number;
  fulfilledQty: number;
  received: number;
  cost: number;
  price: number;
  lineTotal: number;
  status: ProductStatus;
  warehouseStatus: string;
  sku: string;
  installedQty: number;
  statusDate: number;
  isPrivate: boolean;
  isDeleted: boolean;
  totalRequestedQty: number;
}

export interface TimeMaterialPhaseRequest {
  title: string;
  menuOrder: number;
  date: number;
}

export interface TmReportChartSeriesResponse {
  name: string;
  //   type: string;
  data: number[];
}

export interface CompletedLineResponse {
  checked: boolean;
  quantity: number;
  itemId: EstimateLineItemId;
}

export interface TimeMaterialItemsCanaResponse {
  status: boolean;
  items: VeryCompactItemsCanaStatusResponse[];
}

export interface VeryCompactItemsCanaStatusResponse {
  sku: EstimateLineItemId;
  status: string;
}

export interface TimeMaterialWmsDemandResponse {
  id: string;
  timeMaterialId: TimeMaterialId;
  canUnRequest: boolean;
  status: string;
  orderNumber: string;
  items: TimeMaterialWmsDemandItem[];
}

export interface TimeMaterialWmsDemandItem {
  id: string;
  qty: number;
  sku: string;
  title: string;
  description: string;
  installed: number;
  unitPrice: number;
  requestedQty: number;
  totalRequestedQty: number;
}

export interface TMEventResposne {
  title: string;
  description: string;
  calendarId: string;
  id: string;
  location: string;
  userId: UserId;
  start: number;
  end: number;
  participants: { email: string; status: string }[];
  organizer: { name: string; email: string };
  link: string;
  status: string;
}
